import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import * as _ from 'lodash';
import { map } from 'rxjs';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { DateFilterListComponent } from 'src/app/shared/components/date-filter-list/date-filter-list.component';
import { IdsFilterListComponent } from 'src/app/shared/components/ids-filter-list/ids-filter-list.component';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { StringFilterListComponent } from 'src/app/shared/components/string-filter-list/string-filter-list.component';
import { MediaShareLinkQueries } from 'src/app/shared/types/get-query';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { Paginable } from 'src/app/shared/types/paginable';
import { environment } from 'src/environments/environment';
import { DateRange } from '../../models/new/dateRange';
import { MediaShareLink, MediaShareLinkFilters } from '../../models/new/media-share-link';
import { CreateMediaShareLinkInput } from '../../models/types/create-media-share-link-input.type';
import { MediaShareLinkMapper } from '../../services/media-share-link-mapper';
import { CreateMediaShareLinkModalComponent } from './create-media-share-link-modal/create-media-share-link-modal.component';

@Component({
  standalone: false,
  selector: 'bt-company-media-share-link-list',
  templateUrl: './company-media-share-link-list.component.html',
  styleUrls: ['./company-media-share-link-list.component.scss']
})
export class CompanyMediaShareLinkListComponent implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #graphQlService = inject(GraphQLService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #utilitiesService = inject(UtilitiesService);
  readonly #mediaShareLinkMapper = inject(MediaShareLinkMapper);
  readonly #dialog = inject(MatDialog);

  mediaShareLinks: Paginable<MediaShareLink> = new Paginable<MediaShareLink>();
  mediaShareLinkFilters = new MediaShareLinkFilters();
  loading = false;

  ngOnInit(): void {
    this.#route.queryParams.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((params: Params) => {
      this.mediaShareLinkFilters = this.#mediaShareLinkMapper.urlToFilters(params);
      this.fetchMediaShareLinks();
    });
  }

  getShareLink(id: string | number): string {
    const baseUrl = (environment.frontendUrl || '').replace(/\/$/, '');
    return `${baseUrl}/share-media/${id}`;
  }

	copyShareLink(id: string | number): void {
		const text = this.getShareLink(id);

		if (navigator.clipboard?.writeText) {
			navigator.clipboard.writeText(text).then(
				() => {
					this.#utilitiesService.showSnackbar('Link copied to clipboard', SnackbarType.Success);
				},
				() => {
					this.copyShareLinkFallback(text);
				}
			);
			return;
		}

		this.copyShareLinkFallback(text);
	}

	private copyShareLinkFallback(text: string): void {
		const textarea = document.createElement('textarea');
		textarea.value = text;
		textarea.setAttribute('readonly', '');
		textarea.style.position = 'fixed';
		textarea.style.left = '-9999px';
		document.body.appendChild(textarea);
		textarea.select();

		try {
			const copied = document.execCommand('copy');
			this.#utilitiesService.showSnackbar(
				copied ? 'Link copied to clipboard' : 'Error copying link',
				copied ? SnackbarType.Success : SnackbarType.Error
			);
		} catch {
			this.#utilitiesService.showSnackbar('Error copying link', SnackbarType.Error);
		} finally {
			document.body.removeChild(textarea);
		}
	}

  openLinkInANewWindow(url: string): void {
    window.open(url, '_blank');
  }

  fetchMediaShareLinks() {
    this.loading = true;
    this.mediaShareLinks.currentPage = this.mediaShareLinkFilters.page || 1;
    this.mediaShareLinks.size = this.mediaShareLinkFilters.size || 20;

    const pagination = this.mediaShareLinks.buildQuery(this.mediaShareLinks.currentPage);

    this.#graphQlService
      .getData(MediaShareLinkQueries.GET_MEDIA_SHARE_LINKS, {
        where: this.#mediaShareLinkMapper.toWhereClause(this.mediaShareLinkFilters),
        ...pagination
      })
      .pipe(
        map((res: any) => {
          const mediaShareLinks = res.data.mediaShareLinks;

          return new Paginable<MediaShareLink>({
            items: (mediaShareLinks.nodes || []).map((node: any) => this.#mediaShareLinkMapper.toMediaShareLink(node)),
            totalCount: mediaShareLinks.totalCount,
            currentPage: this.mediaShareLinks.currentPage,
            size: this.mediaShareLinks.size
          });
        })
      )
      .subscribe({
        next: (data: Paginable<MediaShareLink>) => {
          this.mediaShareLinks = data;
          this.loading = false;
        },
        error: (err) => {
          this.#utilitiesService.showSnackbar('Error fetching media share links', SnackbarType.Error);
          this.loading = false;
          console.error(err);
        }
      });
  }

  openCreateMediaShareLinkModal() {
    this.#dialog
      .open(CreateMediaShareLinkModalComponent)
      .afterClosed()
      .subscribe(
        (
          result: {
            modelColors: Array<{ modelId: number; colorSizes: Array<{ colorId: number }> }>;
          } | null
        ) => {
          if (!result?.modelColors?.length) {
            return;
          }

          const createMediaShareLinkInput: CreateMediaShareLinkInput = {
            modelColors: result.modelColors
          };

          this.loading = true;
          this.#graphQlService
            .postData(MutationQuery.CREATE_MEDIA_SHARE_LINK, { createMediaShareLinkInput })
            .subscribe({
              next: (res: any) => {
                const payload = res?.data?.createMediaShareLink;
                if (payload?.success === false) {
                  this.loading = false;
                  this.#utilitiesService.showSnackbar(
                    payload.message || 'Error creating media share link',
                    SnackbarType.Error
                  );
                  return;
                }

                this.#utilitiesService.showSnackbar(
                  payload?.message || 'Media share link created successfully',
                  SnackbarType.Success
                );
                this.fetchMediaShareLinks();
              },
              error: (err) => {
                this.loading = false;
                this.#utilitiesService.showSnackbar(
                  err?.networkError?.error?.errors?.[0]?.message || 'Error creating media share link',
                  SnackbarType.Error
                );
              }
            });
        }
      );
  }

  deactiveMediaShareLink(item: MediaShareLink) {
    if (!item.id) {
      return;
    }

    this.#dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Stop sharing',
          message: 'Are you sure you want to stop sharing this media share link?'
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.loading = true;
        this.#graphQlService
          .postData(MutationQuery.DEACTIVE_MEDIA_SHARE_LINK, {
            deactiveMediaShareLinkInput: { mediaShareLinkId: item.id }
          })
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe({
            next: (res: any) => {
              const payload = res?.data?.deactiveMediaShareLink;
              if (payload?.success === false) {
                this.loading = false;
                this.#utilitiesService.showSnackbar(
                  payload.message || 'Error deactivating media share link',
                  SnackbarType.Error
                );
                return;
              }

              this.#utilitiesService.showSnackbar(
                payload?.message || 'Media share link deactivated successfully',
                SnackbarType.Success
              );
              this.fetchMediaShareLinks();
            },
            error: (err) => {
              this.loading = false;
              this.#utilitiesService.showSnackbar(
                err?.networkError?.error?.errors?.[0]?.message || 'Error deactivating media share link',
                SnackbarType.Error
              );
            }
          });
      });
  }

  stopMediaShareLink(item: MediaShareLink) {
    if (!item.id) {
      return;
    }

    this.#dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Stop Media Share Link',
          message: 'Are you sure you want to stop sharing this media share link?'
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.loading = true;
        this.#graphQlService
          .postData(MutationQuery.DEACTIVE_MEDIA_SHARE_LINK, {
            deactiveMediaShareLinkInput: { mediaShareLinkId: item.id }
          })
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe({
            next: (res: any) => {
              const payload = res?.data?.deactiveMediaShareLink;
              if (payload?.success === false) {
                this.loading = false;
                this.#utilitiesService.showSnackbar(
                  payload.message || 'Error stopping media share link',
                  SnackbarType.Error
                );
                return;
              }

              this.#utilitiesService.showSnackbar(
                payload?.message || 'Media share link stopped successfully',
                SnackbarType.Success
              );
              this.fetchMediaShareLinks();
            },
            error: (err) => {
              this.loading = false;
              this.#utilitiesService.showSnackbar(
                err?.networkError?.error?.errors?.[0]?.message || 'Error stopping media share link',
                SnackbarType.Error
              );
            }
          });
      });
  }

  reactiveMediaShareLink(item: MediaShareLink) {
    if (!item.id) {
      return;
    }

    this.#dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Reactivate Media Share Link',
          message: 'Are you sure you want to reactivate this media share link?'
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.loading = true;
        this.#graphQlService
          .postData(MutationQuery.REACTIVE_MEDIA_SHARE_LINK, {
            reactiveMediaShareLinkInput: { mediaShareLinkId: item.id }
          })
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe({
            next: (res: any) => {
              const payload = res?.data?.reactiveMediaShareLink;
              if (payload?.success === false) {
                this.loading = false;
                this.#utilitiesService.showSnackbar(
                  payload.message || 'Error reactivating media share link',
                  SnackbarType.Error
                );
                return;
              }

              this.#utilitiesService.showSnackbar(
                payload?.message || 'Media share link reactivated successfully',
                SnackbarType.Success
              );
              this.fetchMediaShareLinks();
            },
            error: (err) => {
              this.loading = false;
              this.#utilitiesService.showSnackbar(
                err?.networkError?.error?.errors?.[0]?.message || 'Error reactivating media share link',
                SnackbarType.Error
              );
            }
          });
      });
  }

  deleteMediaShareLink(item: MediaShareLink) {
    if (!item.id) {
      return;
    }

    this.#dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Remove Media Share Link',
          message: 'Are you sure you want to remove this media share link?'
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.loading = true;
        this.#graphQlService
          .postData(MutationQuery.DELETE_MEDIA_SHARE_LINK, {
            deleteMediaShareLinkInput: { mediaShareLinkId: item.id }
          })
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe({
            next: (res: any) => {
              const payload = res?.data?.deleteMediaShareLink;
              if (payload?.success === false) {
                this.loading = false;
                this.#utilitiesService.showSnackbar(
                  payload.message || 'Error removing media share link',
                  SnackbarType.Error
                );
                return;
              }

              this.#utilitiesService.showSnackbar(
                payload?.message || 'Media share link removed successfully',
                SnackbarType.Success
              );
              this.fetchMediaShareLinks();
            },
            error: (err) => {
              this.loading = false;
              this.#utilitiesService.showSnackbar(
                err?.networkError?.error?.errors?.[0]?.message || 'Error removing media share link',
                SnackbarType.Error
              );
            }
          });
      });
  }

  getStylesLabel(item: MediaShareLink): string {
    return (item.styles || [])
      .map((style) => style.code)
      .filter((code): code is string => !!code)
      .join(', ');
  }

  cleanFilter(filter: string) {
    switch (filter) {
      case 'styleCodes':
        this.mediaShareLinkFilters.styleCodes = undefined;
        break;
      case 'link':
        this.mediaShareLinkFilters.link = undefined;
        break;
      case 'creationDate':
        this.mediaShareLinkFilters.creationDate = undefined;
        break;
    }

    this.mediaShareLinkFilters.page = 1;
    this.refreshNavigation();
  }

  openStyleCodeFilterModal() {
    this.#dialog
      .open(IdsFilterListComponent, {
        data: {
          title: 'Style Code',
          selected: this.mediaShareLinkFilters.styleCodes,
          inputType: 'text'
        }
      })
      .afterClosed()
      .subscribe({
        next: (res: string[]) => {
          if (res) {
            this.mediaShareLinkFilters.styleCodes = res;
            this.mediaShareLinkFilters.page = 1;
            this.refreshNavigation();
          }
        }
      });
  }

  openLinkFilterModal() {
    this.#dialog
      .open(StringFilterListComponent, {
        data: { title: 'Link', selected: this.mediaShareLinkFilters.link }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            this.mediaShareLinkFilters.link = _.cloneDeep(res);
            this.mediaShareLinkFilters.page = 1;
            this.refreshNavigation();
          }
        }
      });
  }

  openCreationDateFilterModal() {
    this.#dialog
      .open(DateFilterListComponent, {
        data: {
          title: 'Creation Date',
          selected: _.cloneDeep(this.mediaShareLinkFilters.creationDate)
        },
        width: '400px'
      })
      .afterClosed()
      .subscribe({
        next: (res: DateRange) => {
          if (res) {
            this.mediaShareLinkFilters.creationDate = res;
            this.mediaShareLinkFilters.page = 1;
            this.refreshNavigation();
          }
        }
      });
  }

  pageEvent(event: { pageNumber: number; pageSize: number }) {
    this.mediaShareLinks.currentPage = event.pageNumber;
    this.mediaShareLinks.size = event.pageSize;
    this.mediaShareLinkFilters.page = event.pageNumber;
    this.mediaShareLinkFilters.size = event.pageSize;
    this.refreshNavigation();
  }

  refreshNavigation() {
    this.loading = true;
    this.#router.navigate([location.pathname], {
      queryParams: this.#mediaShareLinkMapper.filtersToUrl(this.mediaShareLinkFilters)
    });
  }
}
